import { type ReactNode } from 'react'
import { Head, router } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'

type SettingType = 'boolean'

type SettingRow = {
  key: string
  type: SettingType
  label: string
  description: string
  value: boolean | string | number | null
  default: boolean | string | number | null
}

type Props = {
  settings: SettingRow[]
}

export default function AdminSettingsIndex({ settings }: Props) {
  function updateSetting(key: string, value: boolean | string | number) {
    router.patch(
      '/admin/settings',
      { key, value },
      { preserveScroll: true },
    )
  }

  return (
    <>
      <Head title="Admin - Settings" />
      <div className="max-w-2xl">
        <h1 className="text-3xl font-semibold tracking-tight font-serif">Settings</h1>
        <p className="mt-2 text-base text-muted-foreground">
          Toggle app-wide behaviors. Changes take effect immediately.
        </p>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>App settings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-black/5">
              {settings.map(setting => (
                <div
                  key={setting.key}
                  className="flex items-start justify-between gap-4 py-4 first:pt-0 last:pb-0"
                >
                  <div className="space-y-1">
                    <Label htmlFor={`setting-${setting.key}`} className="text-base font-medium">
                      {setting.label}
                    </Label>
                    <p className="text-sm text-muted-foreground">{setting.description}</p>
                  </div>
                  {setting.type === 'boolean' && (
                    <Switch
                      id={`setting-${setting.key}`}
                      checked={Boolean(setting.value)}
                      onCheckedChange={checked => updateSetting(setting.key, checked)}
                    />
                  )}
                </div>
              ))}
              {settings.length === 0 && (
                <p className="py-8 text-center text-muted-foreground">No settings available.</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

AdminSettingsIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
